import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';

// Uma tela de orcamento aberta por vez, por usuario (pedido dele, 03/10/2026): a loja abria a mesma
// venda em duas abas e terminava com dois orcamentos, as vezes os dois finalizados.
//
// A trava e' um Web Lock do navegador, nao uma caneta no banco: o navegador SOLTA o lock sozinho
// quando a aba fecha, recarrega ou trava, entao nao ha TTL pra esperar nem aba fantasma prendendo a
// tela. O limite e' o alcance: vale entre abas do MESMO navegador — outro computador ou o tablet nao
// enxergam este lock.
//
// A aba barrada fica na fila do lock: quando a outra fecha, esta abre sozinha. E o formulario so
// MONTA depois do lock — em /orcamentos/novo o orcamento nasce no 1o autosave, entao nada e' criado
// numa aba barrada.
//
// Navegador sem Web Locks (Safari < 15.4) abre sem trava: falhar fechado ali tiraria o orcamento
// de quem nao tem como atualizar o aparelho.

type Estado = 'pedindo' | 'minha' | 'ocupada';

// O que o lock precisa do navegador. `navigator.locks` existe nos tipos do DOM, mas nao em todo
// aparelho — por isso a leitura opcional.
type LockManagerMinimo = {
  request(nome: string, opcoes: { signal: AbortSignal }, cb: () => Promise<void>): Promise<unknown>;
};

export function TelaUnicaOrcamento({ children }: { children: ReactNode }) {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [estado, setEstado] = useState<Estado>('pedindo');

  useEffect(() => {
    if (!usuario) return;
    // Admin fica de fora (pedido dele, 03/10/2026): confere varios orcamentos em abas lado a lado.
    if (usuario.perfil === 'admin') { setEstado('minha'); return; }
    const locks = (navigator as Navigator & { locks?: LockManagerMinimo }).locks;
    if (!locks?.request) { setEstado('minha'); return; }

    const ctrl = new AbortController();
    let soltar: () => void = () => {};
    // Entre duas telas de orcamento na MESMA aba (lista -> /novo -> /:id) a anterior solta e esta
    // pede no mesmo instante; sem a espera curta, a tarja de "outra aba" piscaria na troca.
    const tarja = setTimeout(() => setEstado((e) => (e === 'pedindo' ? 'ocupada' : e)), 500);

    locks
      .request(`fiodecobre-tela-orcamento-${usuario.id}`, { signal: ctrl.signal }, () => new Promise<void>((res) => {
        soltar = res;
        clearTimeout(tarja);
        setEstado('minha');
      }))
      // AbortError ao sair da tela antes de ganhar o lock — esperado.
      .catch(() => {});

    return () => {
      clearTimeout(tarja);
      ctrl.abort();
      soltar();
    };
  }, [usuario?.id, usuario?.perfil]);

  if (estado === 'minha') return <>{children}</>;
  if (estado === 'pedindo') return null;

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo">Orcamento</h2>
      </div>
      <div className="flex justify-center p-8">
      <div
        className="rounded-lg shadow p-6 max-w-lg w-full"
        style={{ backgroundColor: 'var(--panel-bg)', color: 'var(--panel-fg)' }}
      >
        <p className="text-lg font-bold mb-3">Voce ja tem um orcamento aberto em outra aba</p>
        <p className="mb-2">
          So e' possivel trabalhar em um orcamento por vez. Volte na outra aba para continuar, ou feche-a.
        </p>
        <p className="mb-5">Assim que a outra aba for fechada, esta tela abre sozinha.</p>
        <button type="button" className="btn-primario" onClick={() => navigate('/orcamentos')}>
          Voltar para a lista de orcamentos
        </button>
      </div>
      </div>
    </div>
  );
}
